import type { EvalContext } from '../EvalContext.ts';
import { evalExpr } from '../evaluator.ts';
import type { Value } from '../value.ts';
import { type EvalDeps, useEvalDeps } from './EvalDepsContext.ts';
import { useSession } from './SessionContext.ts';

/**
 * Build an {@link EvalContext} from a mounted `Session` and the
 * optional fields on {@link EvalDeps}. Shared by every React-side
 * evaluator hook (`useEvalExpr`, `useMaterialize`,
 * `useMaterializedExpr`) — single source of truth for "spread deps
 * onto a base EvalContext."
 */
export function buildEvalContext(session: EvalContext['session'], deps: EvalDeps): EvalContext {
  return {
    session,
    ...(deps.sourceFetchers === undefined ? {} : { sourceFetchers: deps.sourceFetchers }),
    ...(deps.getCogniteClient === undefined ? {} : { getCogniteClient: deps.getCogniteClient }),
    ...(deps.getViews === undefined ? {} : { getViews: deps.getViews }),
  };
}

/**
 * React hook returning an async `(expr) => Promise<Value>` bound to
 * the `Session` from `SessionContext` and the {@link EvalDeps} read
 * from `EvalDepsContext` (defaults applied when no provider is
 * mounted). This is the canonical entry point for evaluating
 * s-expressions inside React — assembles an `EvalContext` from React
 * state and threads it into `evalExpr`.
 *
 * The returned function is stable per session reference — React
 * Compiler memoizes the closure, so this hook can be called in render
 * without churning effect dependency arrays.
 *
 * @throws via `useSession()` if no `<SessionContext>` provider is
 *   mounted upstream (intentional hard failure — see `SessionContext`
 *   rationale). Missing `EvalDepsContext` is *not* an error;
 *   `useEvalDeps()` falls back to engine defaults.
 *
 * @see §7.11 — React integration design
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const evaluate = useEvalExpr();
 *   const onClick = async () => {
 *     const result = await evaluate('(+ 1 2)');
 *     console.log(result); // 3
 *   };
 *   return <button onClick={onClick}>Run</button>;
 * }
 * ```
 */
export function useEvalExpr(): (expr: string) => Promise<Value> {
  const session = useSession();
  const deps = useEvalDeps();
  return (expr: string) => evalExpr(expr, buildEvalContext(session, deps), deps.ops, deps.verbs);
}
